import { alpha } from '@mui/material/styles';
import styled, { createGlobalStyle } from 'styled-components';

import { MuiTheme } from 'theme';
import { colors } from 'theme/colors';

export const HelpLink = styled.a`
  align-self: flex-start;
  font-size: var(--knock-text-3, 1.4rem);
  color: ${colors.primary.main};
  text-decoration: none;
`;

// vertical-align: middle centres the arrow against the text instead of the inline box's top.
export const LearnMoreArrow = styled.span`
  display: inline-flex;
  vertical-align: middle;
  color: inherit;
  text-decoration: none;

  svg {
    display: block;
  }
`;

// Body copy and its help link sit side by side instead of stacked.
export const BodyRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: var(--knock-spacing-6);

  .knock-guide-card__body,
  .knock-guide-banner__body,
  .knock-guide-modal__body {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

export const InlineHelpLink = styled(HelpLink)`
  flex: 0 0 auto;
  font-weight: 500;
  white-space: nowrap;
`;

// Bleeds the image to the modal's edges and clips itself, since the modal root can't without also clipping its own shadow and focus ring.
export const EdgeToEdgeImage = styled.a<{ $leading?: boolean }>`
  position: relative;
  display: block;
  overflow: hidden;
  margin-inline: calc(var(--knock-spacing-6) * -1);

  ${({ $leading }) =>
    $leading &&
    `
    margin-top: calc(var(--knock-spacing-6) * -1);
    margin-bottom: var(--knock-spacing-2);
    border-radius: var(--knock-rounded-4) var(--knock-rounded-4) 0 0;

    img {
      /* Cancels Knock's default image margin-top, meant for a trailing not leading image. */
      margin-top: 0;
    }
  `}

  img {
    display: block;
    width: 100%;
    max-width: none;
  }
`;

// The normal inset image treatment — only the modal's leading image bleeds (see EdgeToEdgeImage).
export const CardImage = styled.a`
  display: block;
  margin-top: var(--knock-spacing-2);
  border-radius: var(--knock-rounded-2);
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    margin-top: 0;
  }
`;

// Gives the dismiss cross its own white "ground" when it sits on top of a bled image.
export const OnImageCloseButton = styled.div`
  position: absolute;
  top: var(--knock-spacing-3);
  right: var(--knock-spacing-3);
  z-index: 1;
  border-radius: 50%;
  background: ${alpha(colors.common.white, 0.92)};

  button {
    margin-block: 0;
    padding: 0.3rem; /* 3px padding around Knock's fixed 18px icon = 24px circle. */
  }
`;

// Colour tokens are redeclared under [data-knock-color-mode], not just :root, because Knock's own stylesheet redefines them there too and would otherwise win.
export const KnockGuideTheme = createGlobalStyle`
  :root,
  [data-knock-color-mode] {
    --knock-guide-accent: ${colors.primary.main};
    --knock-guide-accent-dark: ${colors.primary.main};

    --knock-guide-content: ${colors.text.primary};
    --knock-guide-content-light: ${colors.text.secondary};
    --knock-guide-content-disabled: ${colors.text.disabled};
    --knock-guide-content-contrast: ${colors.common.white};

    --knock-guide-border: ${colors.ui.border.main};
    --knock-guide-border-dark: ${colors.ui.border.main};

    --knock-guide-surface: ${colors.background.paper};
    --knock-guide-surface-2: ${colors.background.grey};
  }

  :root {
    --knock-family-sans: ${MuiTheme.typography.fontFamily};

    --knock-text-1: 1.1rem;
    --knock-text-3: 1.4rem;
    --knock-text-4: 2rem;

    --knock-leading-1: 1.6rem;
    --knock-leading-3: 2.1rem;
    --knock-leading-4: 2.6rem;

    --knock-spacing-1: 0.4rem;    /* 4px  */
    --knock-spacing-2: 0.8rem;    /* 8px  */
    --knock-spacing-3: 1.2rem;    /* 12px */
    --knock-spacing-4: 1.6rem;    /* 16px, card and modal padding, flex gaps */
    --knock-spacing-6: 2.4rem;    /* 24px, modal and banner horizontal padding */
    --knock-spacing-8: 3.2rem;    /* 32px, modal viewport inset */
    --knock-spacing-10: 3.6rem;   /* 36px, button height, matches Button size="medium" */
    --knock-spacing-32: 12.8rem;  /* 128px, modal max-height inset */
    --knock-spacing-96: 38.4rem;  /* 384px, card max-width */
    --knock-spacing-140: 56rem;   /* 560px, modal max-width */

    --knock-rounded-2: 0.8rem;    /* 8px, card corner, matches Vatix cards */
    --knock-rounded-3: 0.5rem;    /* 5px, buttons, matches components/Button */
    --knock-rounded-4: 0.8rem;    /* 8px, modal and banner corner */
  }

  /* Titles get Vatix's 600 weight by class, since the shared token also drives buttons at 500. */
  .knock-guide-card__title,
  .knock-guide-banner__title,
  .knock-guide-modal__title {
    font-weight: 600;
  }

  /* Banner/modal headline colour is restated here since Knock's stylesheet only styles the card one. */
  .knock-guide-card__headline,
  .knock-guide-banner__headline,
  .knock-guide-modal__headline {
    color: var(--knock-guide-accent-dark);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: var(--knock-text-1);
    font-weight: var(--knock-weight-medium);
    line-height: var(--knock-leading-1);
  }

  /* Knock's stock banner action is regular weight, unlike card/modal's medium. */
  .knock-guide-banner__action {
    font-weight: var(--knock-weight-medium);
  }

  .knock-guide-card__message,
  .knock-guide-modal__header {
    gap: var(--knock-spacing-1);
  }

  /* Knock's stylesheet gives this element no display:flex, so gap alone is inert here. */
  .knock-guide-banner__message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--knock-spacing-1);
  }

  /* Knock's stock header already has padding-bottom; cancel it so our margin-top is the only 4px. */
  .knock-guide-modal__header {
    padding-bottom: 0;
  }

  /* The modal has no flex-column wrapper like the card's, so header/title/body need their own gaps. */
  .knock-guide-modal__header + .knock-guide-modal__title {
    margin-top: var(--knock-spacing-1);
  }

  .knock-guide-modal__title + .knock-guide-modal__body,
  .knock-guide-modal__title + div:not(.knock-guide-modal__actions) {
    margin-top: var(--knock-spacing-1);
  }

  .knock-guide-modal__title + a + div:not(.knock-guide-modal__actions) {
    margin-top: var(--knock-spacing-2);
  }

  /* Actions right-align, following CustomModal, overriding Knock's left/space-between defaults. */
  .knock-guide-card__actions,
  .knock-guide-banner__actions,
  .knock-guide-modal__actions {
    justify-content: flex-end;
  }

  .knock-guide-card__actions[data-with-divider='true'],
  .knock-guide-modal__actions[data-with-divider='true'] {
    padding-top: var(--knock-spacing-4);
    border-top: 1px solid ${colors.ui.border.main};
  }

  /* Knock stretches the modal's primary action to width:100%, which reads as a full-bleed bar. */
  .knock-guide-modal__action {
    width: auto;
  }

  /* Negative margin keeps the 42px close target without it setting the header row height. */
  .knock-guide-card__close,
  .knock-guide-banner__close,
  .knock-guide-modal__close {
    margin-block: calc(var(--knock-spacing-3) * -1);
  }

  /* Knock pins the banner copy to a 384px min-width, so it stops short on a wide viewport. */
  .knock-guide-banner__message {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* Keeps buttons at their natural width so long body copy squeezes the message, not the actions. */
  .knock-guide-banner__actions {
    flex: 0 0 auto;
  }

  .knock-guide-banner__action {
    flex-shrink: 0;
    white-space: nowrap;
  }

  /* Knock hardcodes a .5px border and no card shadow, so neither is a variable. */
  .knock-guide-card,
  .knock-guide-banner,
  .knock-guide-modal {
    border-width: 1px;
  }

  /* The banner mounts full-width with no padded wrapper, so it needs its own margin to read as a floating card rather than a flush strip. */
  .knock-guide-banner {
    margin: var(--knock-spacing-4) var(--knock-spacing-4) 0;
  }

  /* Matches all four sides, since the close button is already taken out of the flow above. */
  .knock-guide-modal {
    padding: var(--knock-spacing-6);
  }

  .knock-guide-card,
  .knock-guide-banner {
    box-shadow: ${colors.shadows.card};
  }

  /* Secondary actions default to --knock-guide-content (near-black); brand blue instead. */
  .knock-guide-card__action--secondary,
  .knock-guide-banner__action--secondary,
  .knock-guide-modal__action--secondary {
    color: ${colors.primary.main};
  }

  /* Knock ships no hover/focus states; these mirror components/Button. */
  .knock-guide-card__action:hover,
  .knock-guide-banner__action:hover,
  .knock-guide-modal__action:hover {
    background: ${colors.sidebar.itemSelected};
  }

  .knock-guide-card__action--secondary:hover,
  .knock-guide-banner__action--secondary:hover,
  .knock-guide-modal__action--secondary:hover {
    background: ${colors.tint.primary.subtle};
  }

  .knock-guide-card__action:focus-visible,
  .knock-guide-banner__action:focus-visible,
  .knock-guide-modal__action:focus-visible,
  .knock-guide-card__close:focus-visible,
  .knock-guide-banner__close:focus-visible,
  .knock-guide-modal__close:focus-visible {
    outline: 2px solid ${colors.primary.main};
    outline-offset: 2px;
  }
`;
